// Building → Overview (/app/building): the demo's Building Management across every development in the user's scope.
// One call, GET /building/overview, which reads each development through its own Building management tab. The stat
// row doubles as filters (?filter=): the table narrows to the developments with something behind the number.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { useStatFilter } from '../../hooks/useStatFilter';
import { StatTile } from '../charts/StatTile';
import { PageHeader } from '../ui';
import { formatDate, formatMoney } from '../../lib/viz';
import { buildingOverview, hubLink, type DevelopmentBuilding, moneyTile, tileRow } from '../../lib/portfolio';
import { CertChip, Count, Quiet, SectionHead, ShowAll, Th } from './bits';

type Filter = 'overdue' | 'soon' | 'certs' | 'assets' | 'contracts';
const OVERDUE_SHOWN = 12;
const MATCH: Record<Filter, (d: DevelopmentBuilding) => boolean> = {
  overdue: (d) => (d.overdue ?? 0) > 0, soon: (d) => (d.dueIn90 ?? 0) > 0, certs: (d) => (d.certs?.expired ?? 0) > 0,
  assets: (d) => (d.assetsDue ?? 0) > 0, contracts: (d) => (d.contractsPaMinor ?? 0) > 0,
};

export function BuildingOverviewTab() {
  const q = useQuery({ queryKey: ['/building/overview'], queryFn: buildingOverview });
  const tf = useStatFilter<Filter>();
  const [allOverdue, setAllOverdue] = useState(false);
  const o = q.data;

  const header = <PageHeader title="Building management" />;
  if (q.isLoading) return <>{header}<div className="grid gap-2.5" style={tileRow}>{Array.from({ length: 6 }, (_, i) => <div key={i} className="surface h-[78px] animate-pulse" />)}</div></>;
  if (!o) return <>{header}<div className="py-12 text-center text-sm text-ink-400">{q.error ? (q.error as Error).message : 'Nothing to show'}</div></>;

  const s = o.stats; const v = o.visible;
  const f = tf.active && MATCH[tf.active] ? tf.active : null;
  const rows = o.developments.filter((d) => !f || MATCH[f](d));
  const overdue = o.overdue ?? [];
  const shownOverdue = allOverdue ? overdue : overdue.slice(0, OVERDUE_SHOWN);
  const contracts = moneyTile(s.underContractPa);
  const hasTable = v.record || v.assets || v.contracts || v.certificates;

  return (
    <div className="space-y-2.5">
      {header}
      <div className="grid gap-2.5" style={tileRow}>
        <StatTile compact label="Developments" value={s.developments} hint={f ? 'show them all' : 'in your scope'} onClick={tf.clear} />
        {s.overdue !== null && <StatTile compact label="Overdue" value={s.overdue} hint="key dates past" status={s.overdue ? 'critical' : undefined} {...tf.tile('overdue')} />}
        {s.dueIn90 !== null && <StatTile compact label="Due in 90 days" value={s.dueIn90} hint="key dates coming" status={s.dueIn90 ? 'warning' : undefined} {...tf.tile('soon')} />}
        {s.certsExpired !== null && <StatTile compact label="Certificates expired" value={s.certsExpired} status={s.certsExpired ? 'critical' : undefined} {...tf.tile('certs')} />}
        {s.assets !== null && <StatTile compact label="Assets" value={s.assets} hint={s.assetsDue ? `${s.assetsDue} due service` : 'plant logged'} {...tf.tile('assets')} />}
        {s.underContractPa !== null && <StatTile compact label="Under contract pa" value={contracts.value} hint={contracts.hint ?? 'supplier contracts'} {...tf.tile('contracts')} />}
      </div>

      <div className={`grid gap-2.5 ${o.overdue ? 'lg:grid-cols-[minmax(0,1fr)_380px]' : ''}`}>
        <section className="surface min-w-0 overflow-hidden">
          <SectionHead title="By development" note={f ? `${rows.length} of ${o.developments.length} — filtered` : undefined} />
          {!hasTable ? <Quiet>Your role includes the budgets only — they are on the Budgets tab.</Quiet> : !rows.length ? <Quiet>No development has any.</Quiet> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px] text-[12.5px]">
                <thead>
                  <tr className="bg-ink-50/60 text-[11.5px] font-medium uppercase tracking-wide text-ink-500">
                    <th className="px-3 py-1.5 text-left">Development</th>
                    {v.record && <><Th left>Record</Th><Th on={f === 'overdue'}>Overdue</Th><Th on={f === 'soon'}>Next 90 days</Th></>}
                    {v.certificates && <Th left on={f === 'certs'}>Certificates</Th>}
                    {v.assets && <Th on={f === 'assets'}>Assets due</Th>}
                    {v.contracts && <Th on={f === 'contracts'}>Contracts pa</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {rows.map((d) => (
                    <tr key={d.propertyId} className="hover:bg-ink-50/60">
                      <td className="px-3 py-2">
                        <Link to={`/app/properties/${d.propertyId}?tab=building`} className="font-medium text-ink-900 hover:text-bronze-700 hover:underline">{d.name}</Link>
                        {d.city && <div className="text-[11px] text-ink-400">{d.city}</div>}
                      </td>
                      {v.record && <>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-14 overflow-hidden rounded-full bg-ink-100"><div className="h-full rounded-full bg-bronze-500" style={{ width: `${d.recordPct ?? 0}%` }} /></div>
                            <span className="tabular-nums text-ink-700">{d.recordPct ?? 0}%</span>
                          </div>
                        </td>
                        <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.overdue} tone="critical" /></td>
                        <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.dueIn90} /></td>
                      </>}
                      {v.certificates && <td className="whitespace-nowrap px-3 py-1.5"><CertChip c={d.certs} /></td>}
                      {v.assets && <td className="px-3 py-1.5 text-right tabular-nums"><Count n={d.assetsDue} tone="warning" /></td>}
                      {v.contracts && <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums">{d.contractsPaMinor ? formatMoney(d.contractsPaMinor, d.currency) : <span className="text-ink-300">—</span>}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {o.overdue && (
          <aside className="min-w-0">
            <section className="surface overflow-hidden">
              <SectionHead title="Everything overdue" note={String(overdue.length)} />
              {!overdue.length ? <Quiet>Nothing overdue anywhere.</Quiet> : (
                <ul className="divide-y divide-ink-100">
                  {shownOverdue.map((x) => (
                    <li key={x.id}>
                      <Link to={hubLink(x.propertyId, x.link)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-ink-50/60" style={{ boxShadow: 'inset 3px 0 0 #e5484d' }}>
                        <span className="w-[72px] shrink-0 text-[11.5px] tabular-nums text-ink-500">{formatDate(x.date)}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium text-ink-800" title={x.label}>{x.label}</span>
                          <span className="block truncate text-[11.5px] text-ink-400">{x.propertyName}{x.sub ? ` · ${x.sub}` : ''}</span>
                        </span>
                        <span className="shrink-0 text-[11.5px] font-medium tabular-nums text-red-600">{x.daysAgo}d ago</span>
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-300" />
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {overdue.length > OVERDUE_SHOWN && <ShowAll all={allOverdue} total={overdue.length} onToggle={() => setAllOverdue((x) => !x)} />}
            </section>
          </aside>
        )}
      </div>
    </div>
  );
}
